import { FormEvent, useEffect, useState } from "react";
import type { InviteInfo } from "../api/client";

interface RegisterScreenProps {
  code: string;
  initialEmail: string;
  invite: InviteInfo | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  onRegister: (input: {
    code: string;
    email: string;
    password: string;
    displayName: string;
  }) => Promise<void>;
  onBackToLogin: () => void;
}

export function RegisterScreen({
  code,
  initialEmail,
  invite,
  isLoading,
  isSubmitting,
  error,
  onRegister,
  onBackToLogin,
}: RegisterScreenProps) {
  const [email, setEmail] = useState(initialEmail);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const lockedEmail = invite?.email || "";
  const disabled = isLoading || invite?.state !== "open";

  useEffect(() => {
    setEmail(lockedEmail || initialEmail);
  }, [initialEmail, lockedEmail]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== passwordConfirm) {
      return;
    }
    await onRegister({
      code,
      email: email.trim(),
      password,
      displayName: displayName.trim(),
    });
  }

  return (
    <main className="login-screen">
      <section className="login-card">
        <div className="brand-mark">车</div>
        <p className="eyebrow">invite</p>
        <h1>接受邀请</h1>
        <p className="muted">设置密码后，你就可以在 iPhone 上使用车大师。</p>

        {isLoading ? <p className="muted">正在验证邀请...</p> : null}
        {!isLoading && invite?.state !== "open" ? (
          <p className="form-error">
            {invite?.state === "redeemed" ? "这个邀请已经被使用。" : "这个邀请无效或已过期。"}
          </p>
        ) : null}

        <form className="login-form" onSubmit={handleSubmit}>
          <label>
            邀请码
            <input value={code} disabled />
          </label>
          <label>
            邮箱
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={Boolean(lockedEmail) || disabled}
            />
          </label>
          <label>
            昵称
            <input
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={disabled}
              placeholder="可选"
            />
          </label>
          <label>
            设置密码
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={8}
              required
              disabled={disabled}
            />
          </label>
          <label>
            确认密码
            <input
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              minLength={8}
              required
              disabled={disabled}
            />
          </label>
          {password && passwordConfirm && password !== passwordConfirm ? (
            <p className="form-error">两次密码不一致。</p>
          ) : null}
          {error ? <p className="form-error">{error}</p> : null}
          <button
            className="primary-button"
            type="submit"
            disabled={disabled || isSubmitting || password !== passwordConfirm}
          >
            {isSubmitting ? "注册中..." : "注册并登录"}
          </button>
          <button className="text-button" type="button" onClick={onBackToLogin}>
            返回登录
          </button>
        </form>
      </section>
    </main>
  );
}
